import { useState, useEffect, useCallback } from 'react'
import { useWedding } from '../../../context/WeddingContext'
import { supabase } from '../../../lib/supabase'
import { useNavigate } from 'react-router-dom'
import { Plus, X, Trash2, Gift, Crown, Copy, Check, ExternalLink, Share2 } from 'lucide-react'
import { celebrateOnce } from '../../../lib/celebrate'
import PayoutAccountSetup from '../../../components/PayoutAccountSetup'

const PRIORITY = [{ v:'most_needed', l:'⭐ Most Needed' },{ v:'nice_to_have', l:'Nice to Have' }]
const FREE_LIMIT = 5

function AddItemModal({ weddingId, onClose, onAdded, isPremium, count, cs }) {
  const [name, setName]         = useState('')
  const [desc, setDesc]         = useState('')
  const [price, setPrice]       = useState('')
  const [link, setLink]         = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [qty, setQty]           = useState(1)
  const [priority, setPriority] = useState('nice_to_have')
  const [isCash, setIsCash]     = useState(false)
  const [cashTarget, setCashTarget] = useState('')
  const [groupGift, setGroupGift]   = useState(false)
  const [saving, setSaving]         = useState(false)
  const [err, setErr]               = useState('')
  const atLimit = !isPremium && count >= FREE_LIMIT

  async function save() {
    if (!name.trim()) { setErr('Please enter an item name.'); return }
    setSaving(true)
    const { error } = await supabase.from('registry_items').insert({
      wedding_id: weddingId, name: name.trim(),
      description: desc||null, price: price?parseFloat(price):null,
      purchase_link: link||null, image_url: imageUrl||null,
      quantity_needed: qty, priority,
      is_cash_fund: isCash, cash_target: cashTarget?parseFloat(cashTarget):null,
      allow_group_gifting: groupGift, purchased: false,
    })
    if (error) { setErr(error.message); setSaving(false); return }
    await onAdded(); onClose()
    if (count === 0) celebrateOnce(`registry:${weddingId}`, { title: 'Your registry begins 🎁', note: 'The first wish on your list — many beautiful ones to come.' })
  }

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem',backdropFilter:'blur(4px)' }}>
      <div className="card fade-up" style={{ width:'100%',maxWidth:460,padding:'1.75rem',position:'relative',maxHeight:'90vh',overflowY:'auto' }}>
        <button onClick={onClose} style={{ position:'absolute',top:12,right:12,background:'none',border:'none',cursor:'pointer',color:'#BCBCBC' }}><X size={18}/></button>
        <h3 style={{ fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.1rem',margin:'0 0 1.25rem' }}>Add Registry Item</h3>
        {atLimit ? (
          <div style={{ textAlign:'center',padding:'1.5rem 0' }}>
            <Crown size={26} color="#C4826A" style={{ marginBottom:'0.75rem' }}/>
            <p style={{ fontFamily:'Nunito Sans, sans-serif',fontWeight:600,fontSize:'0.875rem',color:'#1A1A1A',marginBottom:'0.5rem' }}>Free limit reached ({FREE_LIMIT} items)</p>
            <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.8rem',color:'#6B6B6B' }}>Upgrade to Premium for unlimited registry items, cash funds, and group gifting.</p>
          </div>
        ) : (
          <div style={{ display:'flex',flexDirection:'column',gap:'0.875rem' }}>
            <div style={{ display:'flex',gap:'0.5rem' }}>
              {[{v:false,l:'🎁 Physical Gift'},{v:true,l:'💵 Cash Fund'}].map(t=>(
                <button key={String(t.v)} onClick={()=>setIsCash(t.v)} style={{ flex:1,padding:'0.625rem',border:`1.5px solid ${isCash===t.v?'#C4826A':'#E5E0D8'}`,background:isCash===t.v?'rgba(196,130,106,0.06)':'white',borderRadius:'0.75rem',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',fontWeight:isCash===t.v?700:500,color:isCash===t.v?'#C4826A':'#6B6B6B' }}>
                  {t.l}
                </button>
              ))}
            </div>
            <div><label className="label">Item Name *</label><input className="input-field" placeholder={isCash ? 'e.g. Honeymoon Fund' : 'e.g. Kitchen Aid Mixer'} value={name} onChange={e=>setName(e.target.value)} autoFocus/></div>
            <div><label className="label">Description</label><input className="input-field" placeholder="Optional details" value={desc} onChange={e=>setDesc(e.target.value)}/></div>
            {!isCash ? (
              <>
                <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.625rem' }}>
                  <div><label className="label">Price ({cs})</label><input className="input-field" type="number" placeholder="0" value={price} onChange={e=>setPrice(e.target.value)}/></div>
                  <div><label className="label">Quantity Needed</label><input className="input-field" type="number" min="1" value={qty} onChange={e=>setQty(parseInt(e.target.value)||1)}/></div>
                </div>
                <div><label className="label">Purchase Link</label><input className="input-field" type="url" placeholder="https://..." value={link} onChange={e=>setLink(e.target.value)}/></div>
                <div><label className="label">Image URL (optional)</label><input className="input-field" type="url" placeholder="https://..." value={imageUrl} onChange={e=>setImageUrl(e.target.value)}/></div>
                {isPremium && (
                  <label style={{ display:'flex',alignItems:'center',gap:'0.5rem',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem' }}>
                    <input type="checkbox" checked={groupGift} onChange={e=>setGroupGift(e.target.checked)} style={{ accentColor:'#C4826A' }}/>
                    Allow group gifting (multiple guests contribute)
                  </label>
                )}
              </>
            ) : (
              <>
                <div><label className="label">Target Amount ({cs})</label><input className="input-field" type="number" placeholder="e.g. 500000" value={cashTarget} onChange={e=>setCashTarget(e.target.value)}/></div>
                {isPremium && (
                  <label style={{ display:'flex',alignItems:'center',gap:'0.5rem',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem' }}>
                    <input type="checkbox" checked={groupGift} onChange={e=>setGroupGift(e.target.checked)} style={{ accentColor:'#C4826A' }}/>
                    Show contribution progress bar to guests
                  </label>
                )}
              </>
            )}
          </div>
        )}
        {err&&<p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.8rem',color:'#C4826A',marginTop:'0.75rem' }}>{err}</p>}
        <div style={{ display:'flex',gap:'0.75rem',marginTop:'1.5rem' }}>
          <button className="btn-secondary" onClick={onClose} style={{ flex:1 }}>Cancel</button>
          {!atLimit&&<button className="btn-primary" onClick={save} disabled={saving} style={{ flex:1,justifyContent:'center' }}>{saving?'Adding…':'Add Item'}</button>}
        </div>
      </div>
    </div>
  )
}

export default function RegistryPage() {
  const { wedding, isPremium } = useWedding()
  const CURRENCY_SYMBOLS = { NGN:'₦', USD:'$', GBP:'£', EUR:'€', GHS:'₵', KES:'KSh', ZAR:'R' }
  const cs = CURRENCY_SYMBOLS[wedding?.currency] || '₦'
  const navigate      = useNavigate()
  const [items, setItems]     = useState([])
  const [reservations, setReservations] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [copied, setCopied]   = useState(false)
  const [deleting, setDeleting] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null) // item pending delete confirmation
  const [statFilter, setStatFilter] = useState('all')      // 'all' | 'reserved' | 'received'

  const guestPortalLink = wedding ? `${window.location.origin}/w/${wedding.slug}` : ''
  const registryLink    = guestPortalLink ? `${guestPortalLink}?tab=registry` : ''

  const fetchAll = useCallback(async () => {
    if (!wedding?.id) return
    setLoading(true)
    const [{ data:i },{ data:r }] = await Promise.all([
      supabase.from('registry_items').select('*').eq('wedding_id',wedding.id).order('created_at',{ascending:false}),
      supabase.from('registry_reservations').select('*, registry_items!inner(wedding_id)').eq('registry_items.wedding_id',wedding.id),
    ])
    setItems(i||[]); setReservations(r||[]); setLoading(false)
  },[wedding?.id])

  useEffect(()=>{ fetchAll() },[fetchAll])

  // Real-time: reflect guest reservations & cash gifts the moment they happen
  useEffect(() => {
    if (!wedding?.id) return
    const ch = supabase.channel(`registry-rt-${wedding.id}`)
      .on('postgres_changes', { event:'*', schema:'public', table:'registry_items',        filter:`wedding_id=eq.${wedding.id}` }, () => fetchAll())
      .on('postgres_changes', { event:'*', schema:'public', table:'registry_reservations', filter:`wedding_id=eq.${wedding.id}` }, () => fetchAll())
      .subscribe()
    return () => supabase.removeChannel(ch)
  }, [wedding?.id, fetchAll])

  async function deleteItem(id) {
    setDeleting(id)
    await supabase.from('registry_items').delete().eq('id',id)
    setItems(prev=>prev.filter(i=>i.id!==id)); setDeleting(null); setConfirmDelete(null)
  }

  async function markReceived(item) {
    const res = reservations.find(r=>r.item_id===item.id&&!r.received)
    if (res) {
      await supabase.from('registry_reservations').update({ received:true,received_at:new Date().toISOString() }).eq('id',res.id)
      // Auto-add to gift log
      await supabase.from('gift_log').insert({
        wedding_id: wedding.id, from_name: res.guest_name || res.reserver_name || 'Guest',
        description: item.name, amount: item.price||null,
        is_cash: item.is_cash_fund, registry_reservation_id: res.id,
        gift_type: item.is_cash_fund?'Cash':'Physical Gift',
        thank_you_sent: false, received_at: new Date().toISOString().slice(0,10),
      })
    }
    await supabase.from('registry_items').update({ purchased:true }).eq('id',item.id)
    await fetchAll()
  }

  function copyLink() { navigator.clipboard.writeText(registryLink); setCopied(true); setTimeout(()=>setCopied(false),2000) }
  async function shareLink() {
    const text = `Browse our wedding registry`
    if (navigator.share) {
      try { await navigator.share({ title:'Our Wedding Registry', text, url:registryLink }) } catch(_) {}
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text + '\n' + registryLink)}`, '_blank')
    }
  }

  const activeItems = items.filter(i=>!i.purchased)
  const receivedItems = items.filter(i=>i.purchased)
  const totalReserved = reservations.filter(r=>!r.received).length

  // Tapping a stat narrows the list to just that category
  const reservedItemIds = new Set(reservations.filter(r=>!r.received).map(r=>r.item_id))
  const visibleActive = statFilter==='received' ? []
    : statFilter==='reserved' ? activeItems.filter(i=>reservedItemIds.has(i.id))
    : activeItems
  const showReceivedSection = statFilter==='all' || statFilter==='received'

  if (!wedding) return null

  return (
    <div style={{ display:'flex',flexDirection:'column',gap:'1.5rem' }}>
      <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexWrap:'wrap',gap:'1rem' }}>
        <div>
          <h1 style={{ fontFamily:'Gibs, serif',fontWeight:400,fontSize:'clamp(1.4rem,3vw,1.8rem)',color:'#1A1A1A',margin:'0 0 0.25rem' }}>Registry</h1>
          <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.875rem',color:'#6B6B6B',margin:0 }}>Guests access the registry through their invite link — no separate link needed.</p>
        </div>
        <button className="btn-primary" onClick={()=>setShowAdd(true)} style={{ display:'flex',alignItems:'center',gap:'0.4rem' }}><Plus size={15}/> Add Item</button>
      </div>

      <PayoutAccountSetup variant="gifts"/>

      {/* Registry Link Card — compact */}
      <div style={{ background:'white',border:'1px solid #E5E0D8',borderRadius:'0.875rem',padding:'0.875rem 1rem',display:'flex',alignItems:'center',gap:'0.75rem' }}>
        <div style={{ width:36,height:36,background:'rgba(196,130,106,0.1)',borderRadius:'0.625rem',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
          <Gift size={16} color="#C4826A"/>
        </div>
        <div style={{ flex:1,minWidth:0 }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.78rem',color:'#1A1A1A',margin:'0 0 0.1rem' }}>Guest registry link</p>
          <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.7rem',color:'#BCBCBC',margin:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{registryLink}</p>
        </div>
        <div style={{ display:'flex',gap:'0.375rem',flexShrink:0 }}>
          <button onClick={copyLink} title="Copy link" style={{ width:32,height:32,background:copied?'rgba(155,175,155,0.12)':'#F3F0EA',border:`1px solid ${copied?'rgba(155,175,155,0.4)':'#E5E0D8'}`,borderRadius:'0.5rem',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:copied?'#9BAF9B':'#6B6B6B',transition:'all 0.15s' }}>
            {copied?<Check size={13}/>:<Copy size={13}/>}
          </button>
          <button onClick={shareLink} title="Share" style={{ width:32,height:32,background:'#F3F0EA',border:'1px solid #E5E0D8',borderRadius:'0.5rem',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#C4826A' }}>
            <Share2 size={13}/>
          </button>
          <a href={registryLink} target="_blank" rel="noopener noreferrer" style={{ width:32,height:32,background:'#F3F0EA',border:'1px solid #E5E0D8',borderRadius:'0.5rem',display:'flex',alignItems:'center',justifyContent:'center',color:'#6B6B6B',textDecoration:'none' }}>
            <ExternalLink size={13}/>
          </a>
        </div>
      </div>

      {/* Stats — tap Reserved / Received to filter the list below */}
      <div style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'0.625rem' }}>
        {[{label:'Total Items',value:items.length,filter:'all'},{label:'Reserved',value:totalReserved,color:'#C4826A',filter:'reserved'},{label:'Received',value:receivedItems.length,color:'#9BAF9B',filter:'received'}].map(s=>{
          const active = statFilter===s.filter
          return (
            <button key={s.label} onClick={()=>setStatFilter(active?'all':s.filter)} style={{ background:active?'rgba(196,130,106,0.06)':'white',border:`1px solid ${active?(s.color||'#C4826A'):'#E5E0D8'}`,borderRadius:'0.875rem',padding:'0.875rem',textAlign:'center',cursor:'pointer',transition:'all 0.15s',boxShadow:active?'0 2px 10px rgba(196,130,106,0.12)':'none' }}>
              <p style={{ fontFamily:'Gibs, serif',fontSize:'1.5rem',color:s.color||'#1A1A1A',margin:'0 0 0.15rem',fontWeight:400 }}>{s.value}</p>
              <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.68rem',color:active?(s.color||'#C4826A'):'#6B6B6B',margin:0,fontWeight:active?700:600 }}>{s.label}</p>
            </button>
          )
        })}
      </div>

      {statFilter!=='all'&&(
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',gap:'0.5rem',background:'#F3F0EA',borderRadius:100,padding:'0.4rem 0.4rem 0.4rem 1rem' }}>
          <span style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.74rem',fontWeight:600,color:'#6B6B6B' }}>Showing {statFilter==='reserved'?'reserved':'received'} items only</span>
          <button onClick={()=>setStatFilter('all')} style={{ display:'flex',alignItems:'center',gap:'0.25rem',background:'white',border:'1px solid #E5E0D8',borderRadius:100,padding:'0.2rem 0.6rem',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',fontWeight:600,color:'#6B6B6B' }}><X size={11}/> Clear</button>
        </div>
      )}

      {/* Free limit bar */}
      {!isPremium&&(
        <div style={{ background:'#F3F0EA',borderRadius:'0.875rem',padding:'0.875rem 1.125rem' }}>
          <div style={{ display:'flex',justifyContent:'space-between',marginBottom:'0.4rem' }}>
            <span style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',fontWeight:600,color:'#1A1A1A' }}>{items.length} of {FREE_LIMIT} items (free)</span>
            <button className="btn-primary" onClick={()=>navigate('/pricing')} style={{ fontSize:'0.7rem',padding:'0.25rem 0.75rem' }}>Upgrade for unlimited</button>
          </div>
          <div style={{ height:5,background:'#E5E0D8',borderRadius:100 }}>
            <div style={{ height:'100%',width:`${Math.min((items.length/FREE_LIMIT)*100,100)}%`,background:'#C4826A',borderRadius:100 }}/>
          </div>
        </div>
      )}

      {/* Active Items */}
      {statFilter==='received'?null:loading?(
        <p style={{ fontFamily:'Nunito Sans, sans-serif',color:'#BCBCBC',textAlign:'center',padding:'2rem' }}>Loading…</p>
      ):visibleActive.length===0?(
        statFilter==='reserved'?(
          <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.875rem',color:'#BCBCBC',textAlign:'center',padding:'2.5rem 1rem' }}>No reserved items yet — guests haven't claimed anything from your registry.</p>
        ):(
        <div style={{ textAlign:'center',padding:'3rem 1rem' }}>
          <div style={{ width:120, height:120, margin:'0 auto 1.25rem', borderRadius:'1.25rem', overflow:'hidden', boxShadow:'0 8px 24px rgba(0,0,0,0.08)', border:'3px solid white' }}>
            <img src="/brand/detail-calendar-600.webp" alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
          </div>
          <h3 style={{ fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.2rem',marginBottom:'0.5rem' }}>No registry items yet</h3>
          <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.875rem',color:'#6B6B6B',marginBottom:'1.5rem' }}>Add the gifts you'd love — from kitchen essentials to honeymoon funds.</p>
          <button className="btn-primary" onClick={()=>setShowAdd(true)} style={{ display:'inline-flex',alignItems:'center',gap:'0.5rem' }}><Plus size={15}/> Add First Item</button>
        </div>
        )
      ):(
        <div style={{ display:'flex',flexDirection:'column',gap:'0.625rem' }}>
          {visibleActive.map(item=>{
            const itemRes = reservations.filter(r=>r.item_id===item.id)
            const reserved = item.quantity_reserved||0
            const needed   = item.quantity_needed||1
            const cashPct  = item.is_cash_fund&&item.cash_target?Math.min(((item.cash_raised||0)/item.cash_target)*100,100):0
            return (
              <div key={item.id} style={{ background:'white',border:'1px solid #E5E0D8',borderRadius:'1rem',padding:'1.125rem' }}>
                <div style={{ display:'flex',gap:'0.875rem',alignItems:'flex-start' }}>
                  {item.image_url&&<img src={item.image_url} alt="" style={{ width:52,height:52,borderRadius:'0.625rem',objectFit:'cover',flexShrink:0 }}/>}
                  <div style={{ flex:1,minWidth:0 }}>
                    <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:'0.5rem',flexWrap:'wrap' }}>
                      <div>
                        <p style={{ fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.9rem',color:'#1A1A1A',margin:0 }}>{item.name}</p>
                        {item.priority==='most_needed'&&<span style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.62rem',fontWeight:700,color:'#C4826A',background:'rgba(196,130,106,0.1)',borderRadius:100,padding:'1px 7px' }}>⭐ Most Needed</span>}
                      </div>
                      <div style={{ display:'flex',gap:'0.4rem' }}>
                        {itemRes.length>0&&(
                          <button onClick={()=>markReceived(item)} style={{ background:'rgba(155,175,155,0.1)',border:'1px solid rgba(155,175,155,0.3)',borderRadius:100,padding:'0.25rem 0.75rem',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontSize:'0.68rem',fontWeight:600,color:'#7A917A' }}>
                            Mark Received
                          </button>
                        )}
                        <button onClick={()=>setConfirmDelete(item)} style={{ background:'none',border:'none',cursor:'pointer',color:'#BCBCBC' }}>{deleting===item.id?'…':<Trash2 size={14}/>}</button>
                      </div>
                    </div>
                    {item.description&&<p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'#6B6B6B',margin:'0.2rem 0 0' }}>{item.description}</p>}
                    {item.price&&<p style={{ fontFamily:'Gibs, serif',fontSize:'1rem',color:'#C4826A',margin:'0.3rem 0 0' }}>{cs}{Number(item.price).toLocaleString()}</p>}
                    {item.is_cash_fund&&item.cash_target&&(
                      <div style={{ marginTop:'0.5rem' }}>
                        <div style={{ height:5,background:'#F3F0EA',borderRadius:100,marginBottom:'0.25rem' }}>
                          <div style={{ height:'100%',width:`${cashPct}%`,background:'#9BAF9B',borderRadius:100 }}/>
                        </div>
                        <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.7rem',color:'#6B6B6B',margin:0 }}>{cs}{Number(item.cash_raised||0).toLocaleString()} of {cs}{Number(item.cash_target).toLocaleString()} ({Math.round(cashPct)}%)</p>
                      </div>
                    )}
                    {needed>1&&<p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',color:'#9BAF9B',margin:'0.25rem 0 0',fontWeight:600 }}>{reserved} of {needed} reserved</p>}
                    {itemRes.length>0&&<div style={{ marginTop:'0.5rem',display:'flex',flexWrap:'wrap',gap:'0.3rem' }}>
                      {itemRes.map(r=><span key={r.id} style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.65rem',color:'#6B6B6B',background:'#F3F0EA',borderRadius:100,padding:'1px 7px' }}>{r.guest_name||r.reserver_name||'Guest'}</span>)}
                    </div>}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Received items */}
      {statFilter==='received'&&receivedItems.length===0&&(
        <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.875rem',color:'#BCBCBC',textAlign:'center',padding:'2.5rem 1rem' }}>Nothing received yet — mark a reserved gift as received once it arrives.</p>
      )}

      {showReceivedSection&&receivedItems.length>0&&(
        <div>
          <p style={{ fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.78rem',color:'#9BAF9B',textTransform:'uppercase',letterSpacing:'0.06em',margin:'0 0 0.625rem' }}>✓ Received ({receivedItems.length})</p>
          <div style={{ display:'flex',flexDirection:'column',gap:'0.4rem',opacity:0.6 }}>
            {receivedItems.map(item=>(
              <div key={item.id} style={{ background:'white',border:'1px solid #E5E0D8',borderRadius:'0.75rem',padding:'0.75rem 1rem',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
                <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',color:'#6B6B6B',margin:0,textDecoration:'line-through' }}>{item.name}</p>
                <span style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.68rem',color:'#9BAF9B',fontWeight:700 }}>Received</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showAdd&&<AddItemModal weddingId={wedding.id} onClose={()=>setShowAdd(false)} onAdded={fetchAll} isPremium={isPremium} count={items.length} cs={cs}/>}

      {confirmDelete&&(
        <div onClick={()=>deleting?null:setConfirmDelete(null)} style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:120,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem',backdropFilter:'blur(4px)' }}>
          <div onClick={e=>e.stopPropagation()} className="card fade-up" style={{ width:'100%',maxWidth:380,padding:'1.75rem',textAlign:'center' }}>
            <div style={{ width:46,height:46,margin:'0 auto 1rem',background:'rgba(196,130,106,0.1)',borderRadius:'0.75rem',display:'flex',alignItems:'center',justifyContent:'center' }}>
              <Trash2 size={20} color="#C4826A"/>
            </div>
            <h3 style={{ fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.2rem',color:'#1A1A1A',margin:'0 0 0.5rem' }}>Delete this item?</h3>
            <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.85rem',color:'#6B6B6B',margin:'0 0 1.5rem',lineHeight:1.5 }}>
              <strong>{confirmDelete.name}</strong> will be removed from your registry. This can't be undone.
            </p>
            <div style={{ display:'flex',gap:'0.75rem' }}>
              <button className="btn-secondary" onClick={()=>setConfirmDelete(null)} disabled={deleting===confirmDelete.id} style={{ flex:1 }}>Cancel</button>
              <button onClick={()=>deleteItem(confirmDelete.id)} disabled={deleting===confirmDelete.id} style={{ flex:1,justifyContent:'center',display:'flex',alignItems:'center',gap:'0.4rem',background:'#C4826A',color:'white',border:'none',borderRadius:'0.75rem',padding:'0.7rem',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.875rem' }}>
                {deleting===confirmDelete.id?'Deleting…':'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
